import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useForm, Controller } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import { FaCcVisa, FaInfoCircle } from 'react-icons/fa';
import { getExchangeRate } from 'redux/settingsSlice';
import { createBookingShuttle, updateBookingShuttle } from 'redux/booking/travel/travelBookingSlice';
import TinymceEditor from 'components/common/TinymceEditor';
import BookingUploadAttachment from '../../BookingUploadAttachment';
import { Drawer, Button, Alert, FormRow, CurrencySelect } from 'components/common';

import { INPUT_CLS as inputClass } from 'helpers/formStyles';
const hint = 'tw-mt-1 tw-text-xs tw-text-slate-500 tw-flex tw-items-center tw-gap-1';

const OtherForm = ({ showModal, setshowModal, booking, record }) => {
  const { control, register, handleSubmit, formState: { errors }, setValue, reset, watch } = useForm();
  const { usdExchangeRateData } = useSelector((state) => state.settings);
  const [files, setFiles] = useState([]);

  const dispatch = useDispatch();
  const showExchangeRate = booking.currency === 'USD';
  const bookingId = booking.id;
  const title = record ? 'Edit' : 'Create';

  useEffect(() => { if (!record) reset(); }, [record, reset]);
  useEffect(() => { if (showExchangeRate) dispatch(getExchangeRate()); }, [showExchangeRate, dispatch]);

  useEffect(() => {
    setValue('name', 'Visa');
    setValue('exchange_rate', usdExchangeRateData);
    setValue('currency', booking.currency);
    if (record) Object.entries(record).forEach(([key, value]) => setValue(key, value));
    // seed only when booking/record change; re-running when the exchange rate loads would overwrite the user's edits
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [setValue, booking, record]);

  const closeModal = () => setshowModal(false);

  const onSubmit = (formData) => {
    const data = { ...formData, other: true, files };
    try {
      if (record) dispatch(updateBookingShuttle({ bookingId, shuttleId: record.id, data }));
      else dispatch(createBookingShuttle({ bookingId, data }));
      closeModal();
    } catch (error) {
      toast.error(error.message);
    }
  };

  return (
    <Drawer
      show={showModal}
      onHide={closeModal}
      title={<><FaCcVisa className="tw-text-blue-600" /> {title} Other booking</>}
      footer={(
        <>
          <Button size="sm" variant="secondary" onClick={closeModal}>Cancel</Button>
          <Button size="sm" type="submit" form="other-form" variant="primary">Save</Button>
        </>
      )}
    >
      <Alert type="warning" className="tw-mb-3">Create a booking for other purposes, such as a visa, for the trip.</Alert>

      <form id="other-form" onSubmit={handleSubmit(onSubmit)}>
        <FormRow label="Name" required error={errors.name?.message}>
          <input placeholder="Name" className={inputClass} {...register('name', { required: 'Name must be required.' })} />
        </FormRow>

        <FormRow label="Description" required error={errors.description?.message}>
          <textarea rows={5} placeholder="Description" className={inputClass} {...register('description', { required: 'Description must be required.' })} />
        </FormRow>

        <hr className="tw-border-slate-200" />

        <FormRow label="Amount estimate currency" required error={errors.amount_estimate_currency?.message}>
          <input placeholder="Amount estimate currency" className={inputClass} {...register('amount_estimate_currency', { required: 'Amount estimate currency must be required.' })} />
          <div className={hint}><FaInfoCircle /> Expense estimate.</div>
        </FormRow>

        <FormRow label="Amount currency">
          <input placeholder="Amount currency" className={inputClass} {...register('amount_currency')} />
          <div className={hint}><FaInfoCircle /> Actual expense.</div>
        </FormRow>

        <FormRow label="Currency" error={errors.currency?.message}>
          <CurrencySelect value={watch('currency')} onChange={(v) => setValue('currency', v, { shouldValidate: true })} />
          <div className={hint}><FaInfoCircle /> Defaults to the trip&apos;s currency; change if this item uses another.</div>
        </FormRow>

        {showExchangeRate && (
          <FormRow label="Exchange rate" error={errors.exchange_rate?.message}>
            <input type="text" placeholder="Exchange rate" className={inputClass} {...register('exchange_rate', { required: 'Exchange rate must be required' })} />
            <Alert type="info" className="tw-mt-2">USD exchange rate to VND from <a href="https://www.vietcombank.com.vn/KHCN/Cong-cu-tien-ich/Ty-gia?devicechannel=default" target="_blank" rel="noreferrer" className="tw-underline">Vietcombank</a>.</Alert>
          </FormRow>
        )}

        <FormRow label="Note">
          <Controller control={control} name="notes"
            render={({ field }) => <TinymceEditor height="30vh" handleChange={field.onChange} value={field.value} />} />
        </FormRow>

        <FormRow label="Attachments">
          <BookingUploadAttachment files={files} setFiles={setFiles} />
        </FormRow>
      </form>
    </Drawer>
  );
};

OtherForm.propTypes = {
  showModal: PropTypes.bool.isRequired,
  setshowModal: PropTypes.func.isRequired,
  booking: PropTypes.object.isRequired,
  record: PropTypes.object,
};

export default OtherForm;
